import { Component, DestroyRef, ElementRef, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, scan, shareReplay, switchMap, tap } from 'rxjs/operators';
import { defaultState, ProductData, ProductSearchService, State } from './product-search.service';

const PAGE_SIZE = 15;

@Component({
  templateUrl: './product-search.component.html',
  styleUrls: ['../common.scss'],
})
export class ProductSearchComponent implements OnInit {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  private _searchTerm = signal('');
  private _nextCursor = signal('');
  private _loading = signal(false);

  state: Signal<State>;
  recentProducts: Signal<ProductData[]>;
  loading = this._loading.asReadonly();

  constructor(private productSearchService: ProductSearchService, private destroyRef: DestroyRef) {
    const searchTerm$ = toObservable(this._searchTerm).pipe(distinctUntilChanged());
    const nextCursor$ = toObservable(this._nextCursor).pipe(distinctUntilChanged());
    const state$ = searchTerm$.pipe(
      switchMap((searchTerm) => {
        return nextCursor$.pipe(
          tap(() => this._loading.set(true)),
          switchMap((nextCursor) => {
            return this.productSearchService.listProductData(searchTerm, nextCursor, PAGE_SIZE).pipe(
              map((response) => {
                return {
                  products: response?.products || [],
                  searchTerm: searchTerm,
                  cursor: response?.cursor || '',
                  hasMore: response?.hasMore || false,
                };
              }),
              shareReplay(1),
            );
          }),
          scan((state, newState) => {
            return {
              ...newState,
              products: state.products.concat(newState.products),
            };
          }, defaultState),
          tap(() => this._loading.set(false)),
        );
      }),
    );
    this.state = toSignal(state$);
    this.recentProducts = this.productSearchService.recentProducts;
  }

  ngOnInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        map(() => this.searchInput.nativeElement.value as string),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchTerm) => {
        this._searchTerm.set(searchTerm);
        this._nextCursor.set('');
      });
  }

  loadMore(nextCursor: string): void {
    this._nextCursor.set(nextCursor);
  }

  switchProduct(product: ProductData): void {
    const { productId } = product;
    const productInfoPath = `/products/${productId}/product-info`;
    window.location.assign(productInfoPath);
  }
}
